
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { migrateExistingUsers, promoteToSuperAdmin } from "@/utils/userMigrationUtils";
import { toast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

const UserMigration = () => {
  const [loading, setLoading] = useState(false);
  const [superAdminEmail, setSuperAdminEmail] = useState("");
  const [superAdminId, setSuperAdminId] = useState("");
  const [promoting, setPromoting] = useState(false);

  const handleMigration = async () => {
    setLoading(true);
    try {
      const result = await migrateExistingUsers();
      
      if (result.success) {
        toast({
          title: "Migration Successful",
          description: result.message,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Migration Failed",
          description: result.message,
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Migration Error",
        description: error.message || "An unknown error occurred",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteSuperAdmin = async () => {
    if (!superAdminId) {
      toast({
        variant: "destructive",
        title: "Missing User ID",
        description: "Please enter a user ID to promote to super admin",
      });
      return;
    }

    setPromoting(true);
    try {
      const result = await promoteToSuperAdmin(superAdminId);
      
      if (result.success) {
        toast({
          title: "Promotion Successful",
          description: result.message,
        });
        // Clear the input after success
        setSuperAdminId("");
      } else {
        toast({
          variant: "destructive",
          title: "Promotion Failed",
          description: result.message,
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Promotion Error",
        description: error.message || "An unknown error occurred",
      });
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>User Migration Tool</CardTitle>
          <CardDescription>
            Migrate existing users to the new user management system by adding them to the user_roles table.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-gray-500">
            This will scan all users in your auth system and create corresponding records in the user_roles table with default permissions.
            Users that already have roles will be skipped.
          </p>
        </CardContent>
        <CardFooter>
          <Button 
            onClick={handleMigration} 
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? "Migrating Users..." : "Migrate All Users"}
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Promote Super Admin</CardTitle>
          <CardDescription>
            Grant super admin privileges to a specific user by their ID.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label htmlFor="userId" className="block text-sm font-medium mb-1">
              User ID
            </label>
            <Input
              id="userId"
              value={superAdminId}
              onChange={(e) => setSuperAdminId(e.target.value)}
              placeholder="Enter user ID"
              className="mb-4"
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button 
            onClick={handlePromoteSuperAdmin} 
            disabled={promoting || !superAdminId}
          >
            {promoting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {promoting ? "Promoting..." : "Promote to Super Admin"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default UserMigration;
